import { useEffect, type RefObject } from 'react';

/**
 * Hook to handle wheel events for scrolling within an element when it is focused.
 * Prevents wheel events from bubbling to parent handlers (like React Flow) when scrolling
 * within the element bounds.
 *
 * @param scrollContainerRef - Ref to the scrollable container element
 */
export const useCaptureElementWheelScroll = (scrollContainerRef: RefObject<HTMLElement | null>) => {
  const container = scrollContainerRef.current;

  useEffect(() => {
    if (!container) {
      return;
    }
    const handleWheel = (event: WheelEvent) => {
      // Ignore pinch gestures (zoom in/out) on Mac - they use ctrlKey modifier
      if (event.ctrlKey) {
        return;
      }

      // Only handle vertical scroll events, ignore horizontal scroll (zoom on Mac)
      // On Mac, horizontal scroll gestures typically have significant deltaX and minimal deltaY
      // We want to capture events where deltaY is meaningful and deltaX is small or zero
      const absDeltaX = Math.abs(event.deltaX);
      const absDeltaY = Math.abs(event.deltaY);

      // Ignore if there's no vertical movement or if deltaX is greater than deltaY
      if (absDeltaY === 0 || absDeltaX > absDeltaY) {
        return;
      }

      // Check if the wheel event is within the container bounds
      const containerRect = container.getBoundingClientRect();
      const isWithinBounds =
        event.clientX >= containerRect.left &&
        event.clientX <= containerRect.right &&
        event.clientY >= containerRect.top &&
        event.clientY <= containerRect.bottom;

      if (isWithinBounds) {
        // Check scrollability at event time so it's always fresh
        if (container.scrollHeight > container.clientHeight) {
          event.stopPropagation();
        }
      }
    };

    // Listen at the capture phase to intercept before React Flow handlers
    container.addEventListener('wheel', handleWheel, true);

    return () => {
      container.removeEventListener('wheel', handleWheel, true);
    };
  }, [container]);
};
